import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * Composable para manejar información de suscripción, plan y módulos
 * Proporciona acceso fácil a la información del plan del usuario
 */
export function useSubscription() {
    const store = useStore();

    // Acciones
    const fetchEffectiveSubscription = () => {
        return store.dispatch('subscription/fetchEffectiveSubscription');
    };

    const fetchActiveModules = (params) => {
        return store.dispatch('subscription/fetchActiveModules', params);
    };

    const fetchAvailablePlans = () => {
        return store.dispatch('subscription/fetchAvailablePlans');
    };

    const startTrial = (data) => {
        return store.dispatch('subscription/startTrial', data);
    };

    const subscribe = (data) => {
        return store.dispatch('subscription/subscribe', data);
    };

    const changePlan = (data) => {
        return store.dispatch('subscription/changePlan', data);
    };

    const updatePaymentMethod = (data) => {
        return store.dispatch('subscription/updatePaymentMethod', data);
    };

    const cancelSubscription = (data) => {
        return store.dispatch('subscription/cancelSubscription', data);
    };

    const reactivateSubscription = () => {
        return store.dispatch('subscription/reactivateSubscription');
    };

    // Getters computados
    const hasActiveSubscription = computed(() =>
        store.getters['subscription/hasActiveSubscription']
    );

    const subscription = computed(() =>
        store.getters['subscription/subscription']
    );

    const subscriptionStatus = computed(() =>
        store.getters['subscription/subscriptionStatus']
    );

    const subscriptionBillingCycle = computed(() =>
        store.getters['subscription/subscriptionBillingCycle']
    );

    const daysRemaining = computed(() =>
        store.getters['subscription/daysRemaining']
    );

    const isInTrial = computed(() =>
        store.getters['subscription/isInTrial']
    );

    const currentPlan = computed(() =>
        store.getters['subscription/currentPlan']
    );

    const planName = computed(() =>
        store.getters['subscription/planName']
    );

    const planCode = computed(() =>
        store.getters['subscription/planCode']
    );

    const planPrice = computed(() =>
        store.getters['subscription/planPrice']
    );

    const planCurrency = computed(() =>
        store.getters['subscription/planCurrency']
    );

    const planModules = computed(() =>
        store.getters['subscription/planModules']
    );

    const activeModules = computed(() =>
        store.getters['subscription/activeModules']
    );

    const activeModuleCount = computed(() =>
        store.getters['subscription/activeModuleCount']
    );

    const includedModules = computed(() =>
        store.getters['subscription/includedModules']
    );

    const additionalModules = computed(() =>
        store.getters['subscription/additionalModules']
    );

    const availablePlans = computed(() =>
        store.getters['subscription/availablePlans']
    );

    const isLoading = computed(() =>
        store.getters['subscription/isLoading']
    );

    const error = computed(() =>
        store.getters['subscription/error']
    );

    // Función helper para verificar si tiene acceso a un módulo
    const hasModule = (moduleCode) => {
        return store.getters['subscription/hasModule'](moduleCode);
    };

    // Función helper para formatear precio
    const formatPrice = (price, currency = 'MXN') => {
        const formatter = new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: currency,
        });
        return formatter.format(price);
    };

    // Función helper para formatear fechas
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Función helper para obtener badge de estado
    const getStatusBadge = (status) => {
        const badges = {
            trialing: { text: 'Período de Prueba', class: 'badge-info' },
            active: { text: 'Activa', class: 'badge-success' },
            past_due: { text: 'Pago Pendiente', class: 'badge-warning' },
            expired: { text: 'Vencida', class: 'badge-danger' },
            cancelled: { text: 'Cancelada', class: 'badge-warning' },
        };
        return badges[status] || { text: 'Sin estado', class: 'badge-secondary' };
    };

    return {
        // Acciones
        fetchEffectiveSubscription,
        fetchActiveModules,
        fetchAvailablePlans,
        startTrial,
        subscribe,
        changePlan,
        updatePaymentMethod,
        cancelSubscription,
        reactivateSubscription,

        // Datos
        hasActiveSubscription,
        subscription,
        subscriptionStatus,
        subscriptionBillingCycle,
        daysRemaining,
        isInTrial,
        currentPlan,
        planName,
        planCode,
        planPrice,
        planCurrency,
        planModules,
        activeModules,
        activeModuleCount,
        includedModules,
        additionalModules,
        availablePlans,
        isLoading,
        error,

        // Helpers
        hasModule,
        formatPrice,
        formatDate,
        getStatusBadge,
    };
}
